import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Accessibility = () => {
  return (
    <div className="pb-20 md:pb-0" data-testid="accessibility-page">
      <SEO
        title="Accessibility Statement"
        description="Havkin Urology is committed to digital accessibility. Read our WCAG 2.1 accessibility statement and learn how to report accessibility issues."
        keywords="accessibility statement, WCAG compliance, website accessibility, Havkin Urology accessibility"
        canonical="/accessibility"
      />
      {/* Hero */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Website Accessibility Statement
          </h1>
          <p className="text-slate-600">Last Updated: February 27, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-slate max-w-none">
          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Our Commitment to Accessibility</h2>
          <p className="text-slate-600 mb-6">
            We are committed to ensuring digital accessibility for people with disabilities. We are continually 
            improving the user experience for everyone and applying relevant accessibility standards to enhance 
            usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible 
            audience, regardless of technology or ability.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Accessibility Standards</h2>
          <p className="text-slate-600 mb-6">
            We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are 
            widely recognized as the industry standard for website accessibility. Accessibility is an ongoing 
            process, and we are actively working toward improving the accessibility and usability of our website.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Accessibility Technology & Tools</h2>
          <p className="text-slate-600 mb-4">
            To support our accessibility efforts, we have deployed industry-standard accessibility technology 
            designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
          </p>
          <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
            <li>Screen reader enhancements</li>
            <li>Keyboard navigation improvements</li>
            <li>Color contrast adjustments</li>
            <li>Text size and spacing controls</li>
            <li>Focus indicators</li>
            <li>Additional accessibility interface options</li>
          </ul>
          <p className="text-slate-600 mb-6">
            This technology helps us improve accessibility while we continue broader auditing, remediation, 
            and development efforts.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Ongoing Improvement</h2>
          <p className="text-slate-600 mb-4">
            Accessibility is an ongoing initiative. We are:
          </p>
          <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
            <li>Reviewing site content and functionality regularly</li>
            <li>Working with developers and accessibility tools</li>
            <li>Identifying and remediating barriers</li>
            <li>Incorporating accessibility into future design and development</li>
          </ul>
          <p className="text-slate-600 mb-6">
            We understand accessibility is a continuous journey and are committed to making improvements over time.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Third-Party Content</h2>
          <p className="text-slate-600 mb-6">
            Some areas of the website may include third-party content, plugins, or integrations that are not 
            fully controlled by us. While we encourage vendors to provide accessible content, we cannot 
            guarantee accessibility of third-party features. However, we welcome feedback regarding any 
            issues encountered.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Feedback & Assistance</h2>
          <p className="text-slate-600 mb-4">
            If you experience difficulty accessing any part of this website, need assistance, or would like 
            to request content in an alternative format, please contact us. Please include:
          </p>
          <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
            <li>The page URL</li>
            <li>Description of the issue</li>
            <li>Your preferred contact method</li>
          </ul>
          <p className="text-slate-600 mb-6">
            <strong>Contact:</strong> Please visit our{' '}
            <Link to="/contact" className="text-primary underline hover:no-underline">Contact Us</Link> page.
          </p>
          <p className="text-slate-600 mb-6">
            We aim to respond within a reasonable timeframe and will make reasonable efforts to provide 
            accessible alternatives.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Good Faith Efforts</h2>
          <p className="text-slate-600 mb-6">
            We are making good-faith efforts to comply with accessibility standards and improve usability 
            for individuals with disabilities. Despite our efforts, some content may not yet fully meet 
            the strictest accessibility standards due to the evolving nature of technology and ongoing 
            updates. We appreciate your understanding as we continue improving accessibility.
          </p>

          <h2 className="font-heading text-2xl font-bold text-primary mb-4">Terms & Conditions — Accessibility Clause</h2>
          <p className="text-slate-600 mb-6">
            We are committed to providing an accessible website experience for all users and strive to 
            follow recognized accessibility standards, including WCAG guidelines. We have implemented 
            industry-standard accessibility tools and continue to review, update, and improve our website 
            to enhance accessibility. Because accessibility is an ongoing process, some areas of the 
            website may not yet fully conform to all accessibility standards. If you encounter accessibility 
            barriers, please contact us so we can provide assistance, offer alternative access, and address 
            the issue where feasible. Nothing in these Terms is intended to limit any rights available 
            under applicable accessibility laws.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Accessibility;
